// ─────────────────────────────────────────────────────────────────────────
// DispatchReturnsSection — CRITICAL FIX #2 (Angela 2026-05-16).
//
// Renders at the top of /dispatch, above the inspector cards.
//   1. Red counter banner (clickable, jumps to the section).
//   2. "Returned Orders" grid of cards:
//        - REMAKE   → "Reassign to inspector"
//        - SG_REVIEW → "Mark as reviewed"
//        - RESCHEDULE → "Send to Scheduling"
//        - Unclassified → "Classify…" (opens modal)
// Polls /api/dispatch/returns + /count every 60s.
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, RefreshCw, Undo2, MapPin, User, Calendar,
  Loader2, X, CheckCircle2, Send, ClipboardEdit, EyeOff, CheckSquare, Square,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_META = {
  REMAKE: {
    label: "REMAKE",
    desc: "Inspector error — back to same inspector",
    pill: "bg-[#C07070]/15 text-[#8B4545] border border-[#C07070]/40",
    badge: "bg-[#C07070] text-white",
  },
  SG_REVIEW: {
    label: "SG REVIEW",
    desc: "Safeguard requested complement",
    pill: "bg-amber-50 text-amber-700 border border-amber-200",
    badge: "bg-amber-500 text-white",
  },
  RESCHEDULE: {
    label: "RESCHEDULE",
    desc: "Customer remarcou / não estava — back to Scheduling",
    pill: "bg-[#2F5D8A]/10 text-[#2F5D8A] border border-[#2F5D8A]/30",
    badge: "bg-[#2F5D8A] text-white",
  },
};

const formatAddress = (o) => {
  const parts = [o.address || o.address1, o.city, o.state].filter(Boolean);
  const tail = o.zip ? ` ${o.zip}` : "";
  return parts.length ? `${parts.join(", ")}${tail}` : "—";
};

const formatDateShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TypePill = ({ type }) => {
  if (!type) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#C07070]/10 text-[#8B4545] border border-[#C07070]/30 animate-pulse"
        data-testid="return-pill-unclassified"
      >
        <AlertTriangle className="h-3 w-3" />
        Unclassified
      </span>
    );
  }
  const meta = TYPE_META[type];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.pill}`}
      data-testid={`return-pill-${type}`}
    >
      {meta.label}
    </span>
  );
};

const ClassifyModal = ({ open, order, onClose, onSubmit, busy }) => {
  const [type, setType] = useState("REMAKE");
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) {
      setType("REMAKE");
      setReason("");
    }
  }, [open]);
  if (!open || !order) return null;
  const submit = (e) => {
    e.preventDefault();
    const r = (reason || "").trim();
    if (!r) {
      toast.error("Reason is required");
      return;
    }
    onSubmit({ order_id: order.id, dispatch_return_type: type, reason: r });
  };
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="classify-modal-backdrop"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5"
        data-testid="classify-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-gray-800">Classify returned order</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Order <code>{order.order_number}</code>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-800"
            data-testid="classify-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
          Return type <span className="text-red-500">*</span>
        </p>
        <div className="space-y-1.5 mb-4">
          {Object.entries(TYPE_META).map(([k, m]) => (
            <label
              key={k}
              className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                type === k ? "border-[#2F5D8A] bg-[#2F5D8A]/5" : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`classify-type-${k}`}
            >
              <input
                type="radio"
                name="rt"
                value={k}
                checked={type === k}
                onChange={() => setType(k)}
                className="mt-1"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800">{m.label}</p>
                <p className="text-[11px] text-gray-500">{m.desc}</p>
              </div>
            </label>
          ))}
        </div>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
          Reason <span className="text-red-500">*</span>
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this order being returned?"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#2F5D8A]"
          data-testid="classify-reason-input"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-800"
            data-testid="classify-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40"
            data-testid="classify-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardEdit className="h-3.5 w-3.5" />}
            Save classification
          </button>
        </div>
      </form>
    </div>
  );
};

const IgnoreModal = ({ open, count, orderNumbers, onClose, onSubmit, busy }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  const isBatch = (count || 0) > 1;
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="ignore-modal-backdrop"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit((reason || "").trim()); }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5"
        data-testid="ignore-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-gray-800">
              {isBatch ? `Ignore ${count} returned orders?` : "Ignore returned order?"}
            </h3>
            <p className="text-xs text-gray-500 mt-1 leading-snug">
              Removes the order{isBatch ? "s" : ""} from this queue without changing payments,
              status, or inspector assignment. The order{isBatch ? "s stay" : " stays"} exactly
              as-is on the inspector's account.
            </p>
            {orderNumbers && orderNumbers.length > 0 && (
              <p className="text-[10px] font-mono text-gray-500 mt-2 max-h-20 overflow-y-auto" data-testid="ignore-modal-order-list">
                {orderNumbers.slice(0, 12).map((n) => `#${n}`).join(", ")}
                {orderNumbers.length > 12 && `, +${orderNumbers.length - 12} more`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-800"
            data-testid="ignore-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 mt-4">
          Reason (optional)
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Internal flipped to external — already correct on inspector account"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#2F5D8A]"
          data-testid="ignore-reason-input"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-800"
            data-testid="ignore-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-900 text-white text-xs font-semibold rounded-lg flex items-center gap-2 disabled:opacity-40"
            data-testid="ignore-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="h-3.5 w-3.5" />}
            {isBatch ? `Ignore ${count} orders` : "Ignore order"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ReturnCard = ({ order, busyId, onClassify, onAction, onIgnore, selected, onToggleSelect }) => {
  const rt = (order.dispatch_return_type || "").toUpperCase();
  const meta = TYPE_META[rt];
  const inspector = order.assigned_inspector || order.qa_returned_to_inspector || "Unassigned";
  const returnedAt = order.dispatch_return_set_at || order.qa_returned_at || order.updated_at;
  const isBusy = busyId === order.id;

  const actionBtn = (() => {
    if (!rt) {
      return (
        <button
          onClick={() => onClassify(order)}
          disabled={isBusy}
          className="w-full px-3 py-2 bg-[#C07070] hover:bg-[#8B4545] text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid={`return-classify-btn-${order.order_number}`}
        >
          <ClipboardEdit className="h-3.5 w-3.5" />
          Classify…
        </button>
      );
    }
    if (rt === "REMAKE") {
      return (
        <button
          onClick={() => onAction(order, "remake-reactivate")}
          disabled={isBusy}
          className="w-full px-3 py-2 bg-[#C07070] hover:bg-[#8B4545] text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid={`return-action-remake-${order.order_number}`}
        >
          {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
          Reassign to inspector
        </button>
      );
    }
    if (rt === "SG_REVIEW") {
      return (
        <button
          onClick={() => onAction(order, "sg-review-clear")}
          disabled={isBusy}
          className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid={`return-action-sg-${order.order_number}`}
        >
          {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          Mark as reviewed
        </button>
      );
    }
    if (rt === "RESCHEDULE") {
      return (
        <button
          onClick={() => onAction(order, "send-to-scheduling")}
          disabled={isBusy}
          className="w-full px-3 py-2 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid={`return-action-resched-${order.order_number}`}
        >
          {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send to Scheduling
        </button>
      );
    }
    return null;
  })();

  return (
    <div
      className={`bg-white rounded-xl border p-3 shadow-sm space-y-2 ${
        selected ? "border-[#2F5D8A] ring-2 ring-[#2F5D8A]/30" : rt ? "border-gray-200" : "border-[#C07070]/50"
      }`}
      data-testid={`return-card-${order.order_number}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <button
            type="button"
            onClick={() => onToggleSelect(order.id)}
            className="mt-0.5 text-gray-400 hover:text-[#2F5D8A] shrink-0"
            data-testid={`return-checkbox-${order.order_number}`}
            aria-label={selected ? "Deselect order" : "Select order"}
          >
            {selected
              ? <CheckSquare className="h-4 w-4 text-[#2F5D8A]" />
              : <Square className="h-4 w-4" />}
          </button>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">
              #{order.order_number}
            </p>
            <p className="text-[11px] text-gray-500 truncate flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              {formatAddress(order)}
            </p>
          </div>
        </div>
        <TypePill type={rt || null} />
      </div>
      <div className="grid grid-cols-2 gap-1 text-[11px] text-gray-600">
        <div className="flex items-center gap-1 truncate">
          <User className="h-3 w-3 text-gray-400 shrink-0" />
          <span className="truncate">{inspector}</span>
        </div>
        <div className="flex items-center gap-1 truncate">
          <Calendar className="h-3 w-3 text-gray-400 shrink-0" />
          <span className="truncate">{formatDateShort(returnedAt)}</span>
        </div>
      </div>
      <p
        className={`text-[11px] italic leading-snug ${
          order.dispatch_return_reason ? "text-gray-700" : "text-[#8B4545]"
        }`}
        data-testid={`return-reason-${order.order_number}`}
      >
        {order.dispatch_return_reason || "No reason yet — needs classification"}
      </p>
      <div className="flex items-stretch gap-1.5">
        <div className="flex-1">{actionBtn}</div>
        <button
          onClick={() => onIgnore(order)}
          disabled={isBusy}
          className="px-2.5 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-40 shrink-0"
          data-testid={`return-ignore-btn-${order.order_number}`}
          title="Remove from queue without taking any action"
        >
          <EyeOff className="h-3.5 w-3.5" />
          Ignore
        </button>
      </div>
    </div>
  );
};

const DispatchReturnsSection = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [byType, setByType] = useState({ REMAKE: 0, SG_REVIEW: 0, RESCHEDULE: 0 });
  const [unclassified, setUnclassified] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [classifyOrder, setClassifyOrder] = useState(null);
  // Angela 2026-05-18 — batch selection + ignore feature.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [ignoreTargets, setIgnoreTargets] = useState(null); // null | { ids: [], numbers: [] }
  const [batchClassifyOpen, setBatchClassifyOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const sectionRef = useRef(null);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dispatch/returns?limit=500`);
      const list = r.data?.items || [];
      setItems(list);
      setTotal(r.data?.total || 0);
      setByType(r.data?.by_type || { REMAKE: 0, SG_REVIEW: 0, RESCHEDULE: 0 });
      setUnclassified(r.data?.unclassified || 0);
      // Drop selections that no longer exist in the list (after refresh).
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev;
        const alive = new Set(list.map((o) => o.id));
        const next = new Set();
        prev.forEach((id) => { if (alive.has(id)) next.add(id); });
        return next;
      });
    } catch (e) {
      console.error("[returns] fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReturns();
    const id = setInterval(fetchReturns, 60000);
    return () => clearInterval(id);
  }, [fetchReturns]);

  const scrollToSection = () => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleClassifySubmit = async (payload) => {
    setBusyId(payload.order_id);
    try {
      await axios.post(`${API}/dispatch/returns/classify`, payload);
      toast.success("Classification saved");
      setClassifyOrder(null);
      fetchReturns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to classify order");
    } finally {
      setBusyId(null);
    }
  };

  const handleAction = async (order, kind) => {
    const verbs = {
      "remake-reactivate": `Reassign #${order.order_number} to its inspector and put it back on today's route?`,
      "sg-review-clear": `Mark #${order.order_number} as reviewed and return to the normal queue?`,
      "send-to-scheduling": `Send #${order.order_number} back to Scheduling? The inspector will lose it for today.`,
    };
    if (!window.confirm(verbs[kind])) return;
    setBusyId(order.id);
    try {
      const r = await axios.post(`${API}/dispatch/returns/${kind}`, { order_id: order.id });
      toast.success(`Done — ${r.data?.action || kind}`);
      fetchReturns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  // ── Selection helpers ──────────────────────────────────────────────────
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllInBucket = useCallback((bucketRows, allSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        bucketRows.forEach((o) => next.delete(o.id));
      } else {
        bucketRows.forEach((o) => next.add(o.id));
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectedOrders = useMemo(
    () => items.filter((o) => selectedIds.has(o.id)),
    [items, selectedIds],
  );

  // ── Ignore handlers ────────────────────────────────────────────────────
  const openIgnoreForOne = (order) => {
    setIgnoreTargets({ ids: [order.id], numbers: [order.order_number] });
  };

  const openIgnoreForSelected = () => {
    if (selectedOrders.length === 0) return;
    setIgnoreTargets({
      ids: selectedOrders.map((o) => o.id),
      numbers: selectedOrders.map((o) => o.order_number),
    });
  };

  const submitIgnore = async (reason) => {
    if (!ignoreTargets || ignoreTargets.ids.length === 0) return;
    setBatchBusy(true);
    if (ignoreTargets.ids.length === 1) setBusyId(ignoreTargets.ids[0]);
    try {
      if (ignoreTargets.ids.length === 1) {
        await axios.post(`${API}/dispatch/returns/ignore`, {
          order_id: ignoreTargets.ids[0], reason,
        });
        toast.success(`Ignored #${ignoreTargets.numbers[0]}`);
      } else {
        const r = await axios.post(`${API}/dispatch/returns/ignore-batch`, {
          order_ids: ignoreTargets.ids, reason,
        });
        toast.success(`Ignored ${r.data?.ignored ?? ignoreTargets.ids.length} orders`);
      }
      setIgnoreTargets(null);
      clearSelection();
      fetchReturns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ignore failed");
    } finally {
      setBatchBusy(false);
      setBusyId(null);
    }
  };

  // ── Batch classify ────────────────────────────────────────────────────
  const submitBatchClassify = async (payload) => {
    if (selectedOrders.length === 0) return;
    setBatchBusy(true);
    try {
      const r = await axios.post(`${API}/dispatch/returns/classify-batch`, {
        order_ids: selectedOrders.map((o) => o.id),
        dispatch_return_type: payload.dispatch_return_type,
        reason: payload.reason,
      });
      toast.success(`Classified ${r.data?.classified ?? selectedOrders.length} orders as ${payload.dispatch_return_type}`);
      setBatchClassifyOpen(false);
      clearSelection();
      fetchReturns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch classify failed");
    } finally {
      setBatchBusy(false);
    }
  };

  const grouped = useMemo(() => {
    // Unclassified first (most urgent), then by type.
    const buckets = { Unclassified: [], REMAKE: [], SG_REVIEW: [], RESCHEDULE: [] };
    items.forEach((o) => {
      const rt = (o.dispatch_return_type || "").toUpperCase();
      if (!rt) buckets.Unclassified.push(o);
      else if (buckets[rt]) buckets[rt].push(o);
    });
    return buckets;
  }, [items]);

  return (
    <>
      {/* Counter banner — only shown when total > 0. */}
      {total > 0 && (
        <button
          onClick={scrollToSection}
          className="w-full flex items-center justify-between gap-3 bg-[#C07070]/10 hover:bg-[#C07070]/15 border-2 border-[#C07070]/40 text-[#8B4545] rounded-xl px-4 py-3 transition-colors"
          data-testid="returns-counter-banner"
        >
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div className="text-left">
              <p className="text-sm font-bold uppercase tracking-wider">
                {total} returned order{total === 1 ? "" : "s"} awaiting action
              </p>
              <p className="text-[11px] text-[#8B4545]/80">
                {unclassified > 0 && <span><b>{unclassified}</b> unclassified · </span>}
                <b>{byType.REMAKE || 0}</b> remake ·{" "}
                <b>{byType.SG_REVIEW || 0}</b> SG review ·{" "}
                <b>{byType.RESCHEDULE || 0}</b> reschedule
              </p>
            </div>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">
            Jump to section →
          </span>
        </button>
      )}

      {/* Returned Orders section (always rendered so refresh button is reachable). */}
      <section
        ref={sectionRef}
        className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
        data-testid="returns-section"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Undo2 className="h-4 w-4 text-[#8B4545]" />
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
              Returned Orders
            </h2>
            <span
              className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wider"
              data-testid="returns-section-total"
            >
              {total}
            </span>
          </div>
          <button
            onClick={fetchReturns}
            className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
            data-testid="returns-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
        {total === 0 ? (
          <p
            className="text-xs text-gray-400 italic"
            data-testid="returns-empty"
          >
            No returned orders right now — everything is in the normal flow.
          </p>
        ) : (
          <div className="space-y-4">
            {selectedIds.size > 0 && (
              <div
                className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 bg-[#2F5D8A]/10 border border-[#2F5D8A]/30 rounded-lg px-3 py-2"
                data-testid="returns-batch-bar"
              >
                <p className="text-xs font-semibold text-[#2F5D8A]" data-testid="returns-batch-count">
                  {selectedIds.size} order{selectedIds.size === 1 ? "" : "s"} selected
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    onClick={openIgnoreForSelected}
                    disabled={batchBusy}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-900 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="returns-batch-ignore-btn"
                  >
                    {batchBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="h-3.5 w-3.5" />}
                    Ignore selected
                  </button>
                  <button
                    onClick={() => setBatchClassifyOpen(true)}
                    disabled={batchBusy}
                    className="px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#264A6E] text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="returns-batch-classify-btn"
                  >
                    <ClipboardEdit className="h-3.5 w-3.5" />
                    Classify selected
                  </button>
                  <button
                    onClick={clearSelection}
                    className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900"
                    data-testid="returns-batch-clear-btn"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            {(["Unclassified", "REMAKE", "SG_REVIEW", "RESCHEDULE"]).map((bucket) => {
              const rows = grouped[bucket];
              if (!rows || rows.length === 0) return null;
              const allSelected = rows.every((o) => selectedIds.has(o.id));
              return (
                <div key={bucket} data-testid={`returns-bucket-${bucket}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      {bucket === "Unclassified" ? "Needs classification" : TYPE_META[bucket].label}
                      <span className="ml-1 text-gray-400 font-normal">({rows.length})</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => selectAllInBucket(rows, allSelected)}
                      className="text-[10px] font-semibold text-[#2F5D8A] hover:text-[#264A6E] flex items-center gap-1 uppercase tracking-wider"
                      data-testid={`returns-select-all-${bucket}`}
                    >
                      {allSelected
                        ? <CheckSquare className="h-3.5 w-3.5" />
                        : <Square className="h-3.5 w-3.5" />}
                      {allSelected ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {rows.map((o) => (
                      <ReturnCard
                        key={o.id}
                        order={o}
                        busyId={busyId}
                        onClassify={setClassifyOrder}
                        onAction={handleAction}
                        onIgnore={openIgnoreForOne}
                        selected={selectedIds.has(o.id)}
                        onToggleSelect={toggleSelect}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ClassifyModal
        open={!!classifyOrder}
        order={classifyOrder}
        onClose={() => setClassifyOrder(null)}
        onSubmit={handleClassifySubmit}
        busy={busyId === classifyOrder?.id}
      />

      <ClassifyModal
        open={batchClassifyOpen}
        order={
          batchClassifyOpen
            ? { id: "__batch__", order_number: `${selectedOrders.length} orders` }
            : null
        }
        onClose={() => setBatchClassifyOpen(false)}
        onSubmit={submitBatchClassify}
        busy={batchBusy}
      />

      <IgnoreModal
        open={!!ignoreTargets}
        count={ignoreTargets?.ids.length || 0}
        orderNumbers={ignoreTargets?.numbers || []}
        onClose={() => setIgnoreTargets(null)}
        onSubmit={submitIgnore}
        busy={batchBusy || (ignoreTargets && busyId === ignoreTargets.ids[0])}
      />
    </>
  );
};

export default DispatchReturnsSection;
